import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { useTranslate, type Identifier } from "ra-core";

import type {
  FinanceAgingPoint,
  FinanceCategoryAmount,
  FinanceCompanyShare,
} from "../../providers/commons/financeMetrics";
import { roundMoney } from "../../providers/commons/financeSchedule";
import { useCategoryLabel } from "../expenses/useCategoryLabel";
import { useCompanyNames } from "../useCompanyNames";
import { useFormatMoney, useFormatPercent } from "../useFormatMoney";
import { ChartCard } from "./ChartCard";
import { ChartTooltip } from "./ChartTooltip";
import { useFinanceColors } from "./useFinanceColors";
import { BAR_RADIUS, buildNivoTheme } from "./chartTheme";

/** Slices shown before the rest folds into "Other": 3 colors validated all-pairs */
const DONUT_SLICES = 3;

type HorizontalDatum = { label: string; amount: number; detail: string };

/** One-series horizontal bars, labelled on the axis */
const HorizontalBars = ({
  data,
  color,
  left = 110,
}: {
  data: HorizontalDatum[];
  color: string;
  left?: number;
}) => {
  const formatMoney = useFormatMoney();
  const colors = useFinanceColors();
  return (
    <ResponsiveBar<HorizontalDatum>
      data={[...data].reverse()}
      keys={["amount"]}
      indexBy="label"
      layout="horizontal"
      margin={{ top: 4, right: 28, bottom: 24, left }}
      padding={0.35}
      borderRadius={BAR_RADIUS}
      colors={[color]}
      valueScale={{ type: "linear", nice: true }}
      enableLabel={false}
      enableGridX
      enableGridY={false}
      axisLeft={{
        tickSize: 0,
        tickPadding: 6,
        format: (value: string) =>
          value.length > 16 ? `${value.slice(0, 15)}…` : value,
      }}
      axisBottom={{
        tickSize: 0,
        tickPadding: 6,
        tickValues: 4,
        format: (value) => formatMoney(Number(value), true),
      }}
      theme={buildNivoTheme(colors)}
      tooltip={({ data: datum }) => (
        <ChartTooltip
          title={datum.label}
          lines={[
            { label: datum.detail, value: formatMoney(datum.amount), color },
          ]}
        />
      )}
    />
  );
};

export const AgingChart = ({ aging }: { aging: FinanceAgingPoint[] }) => {
  const translate = useTranslate();
  const colors = useFinanceColors();
  const formatMoney = useFormatMoney();
  const data = aging.map((point) => ({
    label: translate(`crm.finance.overview.aging.${point.bucket}`),
    amount: point.amount,
    detail: translate("crm.finance.overview.charges_count", {
      smart_count: point.count,
    }),
  }));
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.aging")}
      description={translate("crm.finance.overview.charts.aging_help")}
      table={{
        columns: [
          translate("crm.finance.overview.days_late"),
          translate("crm.finance.overview.series.overdue"),
          translate("crm.finance.overview.count"),
        ],
        rows: aging.map((point, index) => ({
          key: point.bucket,
          cells: [
            data[index].label,
            formatMoney(point.amount),
            String(point.count),
          ],
        })),
      }}
      isEmpty={aging.every((point) => point.count === 0)}
    >
      <HorizontalBars data={data} color={colors.overdue} left={90} />
    </ChartCard>
  );
};

export const ConcentrationChart = ({
  concentration,
}: {
  concentration: FinanceCompanyShare[];
}) => {
  const translate = useTranslate();
  const colors = useFinanceColors();
  const formatMoney = useFormatMoney();
  const formatPercent = useFormatPercent();
  const names = useCompanyNames(concentration);
  const nameOf = (id: Identifier) => names.get(id) ?? `#${id}`;
  const data = concentration.map((item) => ({
    label: nameOf(item.company_id),
    amount: item.mrr,
    detail: translate("crm.finance.overview.share_of_mrr", {
      share: formatPercent(item.share),
    }),
  }));
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.concentration")}
      description={translate("crm.finance.overview.charts.concentration_help")}
      table={{
        columns: [
          translate("crm.finance.fields.company"),
          translate("crm.finance.overview.series.mrr"),
          translate("crm.finance.overview.share"),
        ],
        rows: concentration.map((item) => ({
          key: String(item.company_id),
          cells: [
            nameOf(item.company_id),
            formatMoney(item.mrr),
            formatPercent(item.share),
          ],
        })),
      }}
      height={300}
      isEmpty={concentration.length === 0}
    >
      <HorizontalBars data={data} color={colors.recurring} />
    </ChartCard>
  );
};

export const ExpensesByCategoryChart = ({
  categories,
}: {
  categories: FinanceCategoryAmount[];
}) => {
  const translate = useTranslate();
  const colors = useFinanceColors();
  const formatMoney = useFormatMoney();
  const formatPercent = useFormatPercent();
  const categoryLabel = useCategoryLabel();
  const total = categories.reduce((sum, item) => sum + item.amount, 0);
  const top = categories.slice(0, DONUT_SLICES);
  const rest = roundMoney(
    categories.slice(DONUT_SLICES).reduce((sum, item) => sum + item.amount, 0),
  );
  const slices = [
    ...top.map((item, index) => ({
      id: item.category,
      label: categoryLabel(item.category),
      value: item.amount,
      color: [colors.recurring, colors.setup, colors.received][index],
    })),
    ...(rest > 0
      ? [
          {
            id: "__other",
            label: translate("crm.finance.overview.other"),
            value: rest,
            color: colors.other,
          },
        ]
      : []),
  ];
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.expenses")}
      description={translate("crm.finance.overview.charts.expenses_help")}
      legend={slices.map((slice) => ({
        label: slice.label,
        color: slice.color,
      }))}
      table={{
        columns: [
          translate("crm.finance.expenses.fields.category"),
          translate("crm.finance.fields.amount"),
          translate("crm.finance.overview.share"),
        ],
        rows: categories.map((item) => ({
          key: item.category,
          cells: [
            categoryLabel(item.category),
            formatMoney(item.amount),
            formatPercent(total > 0 ? item.amount / total : 0),
          ],
        })),
      }}
      isEmpty={categories.length === 0}
    >
      <ResponsivePie
        data={slices}
        margin={{ top: 12, right: 12, bottom: 12, left: 12 }}
        innerRadius={0.62}
        padAngle={1}
        cornerRadius={4}
        colors={{ datum: "data.color" }}
        borderWidth={0}
        enableArcLinkLabels={false}
        enableArcLabels={false}
        activeOuterRadiusOffset={4}
        theme={buildNivoTheme(colors)}
        tooltip={({ datum }) => (
          <ChartTooltip
            title={String(datum.label)}
            lines={[
              {
                label: formatPercent(total > 0 ? datum.value / total : 0),
                value: formatMoney(datum.value),
                color: datum.color,
              },
            ]}
          />
        )}
      />
    </ChartCard>
  );
};
